import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getProject, updateProject, type Project, type UpdateProjectData } from '../../api/projects';
import { getProjectUsage, type ProjectUsage } from '../../api/usage';
import { StatusBadge } from '../common/StatusBadge';
import { Modal } from '../common/Modal';
import { ProjectForm } from './ProjectForm';
import { ApiKeysPanel } from './ApiKeysPanel';
import { SpendChart } from '../usage/SpendChart';

export function ProjectDetailView() {
  const { projectId } = useParams<{ projectId: string }>();
  const { getJwtToken } = useAuth();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [usage, setUsage] = useState<ProjectUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getJwtToken();
      if (!token) return;
      const [projectData, usageData] = await Promise.all([
        getProject(projectId, token),
        getProjectUsage(projectId, token).catch(() => null),
      ]);
      setProject(projectData);
      setUsage(usageData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [projectId, getJwtToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdate = async (data: UpdateProjectData | Record<string, unknown>) => {
    if (!projectId) return;
    const token = await getJwtToken();
    if (!token) return;
    await updateProject(projectId, data as UpdateProjectData, token);
    setShowEditModal(false);
    await fetchData();
  };

  if (loading && !project) {
    return <div className="view__empty">Loading project details...</div>;
  }

  if (!project) {
    return (
      <div className="view__empty">
        <p>Project not found.</p>
        <button className="btn btn--secondary" onClick={() => navigate('/admin/projects')} type="button">
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">{project.name}</h1>
        <div className="view__actions">
          <button
            className="btn btn--primary"
            onClick={() => setShowEditModal(true)}
            type="button"
          >
            Edit
          </button>
          <button
            className="btn btn--secondary"
            onClick={() => navigate('/admin/projects')}
            type="button"
          >
            Back
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      <section className="view__section">
        <h2 className="view__section-title">Project Information</h2>
        <div className="settings-card">
          <div className="settings-card__row">
            <span className="settings-card__label">Project ID</span>
            <span className="settings-card__value">
              <code>{project.projectId}</code>
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Name</span>
            <span className="settings-card__value">{project.name}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Description</span>
            <span className="settings-card__value">{project.description || '--'}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Team</span>
            <span className="settings-card__value">{project.teamId || '--'}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Owner</span>
            <span className="settings-card__value">{project.owner}</span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Status</span>
            <StatusBadge status={project.enabled ? 'Active' : 'Disabled'} />
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Max Budget</span>
            <span className="settings-card__value">
              {project.maxBudget != null ? `$${project.maxBudget.toFixed(2)}` : 'Unlimited'}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Models</span>
            <span className="settings-card__value">
              {project.models && project.models.length > 0
                ? project.models.join(', ')
                : 'All'}
            </span>
          </div>
          <div className="settings-card__row">
            <span className="settings-card__label">Created</span>
            <span className="settings-card__value">{new Date(project.createdAt).toLocaleString()}</span>
          </div>
        </div>
      </section>

      {/* API Keys */}
      {projectId && <ApiKeysPanel projectId={projectId} />}

      {/* Spend chart */}
      <section className="view__section">
        <h2 className="view__section-title">Spend (Last 30 Days)</h2>
        {usage && usage.dailySpend && usage.dailySpend.length > 0 ? (
          <div className="chart-container">
            <SpendChart
              data={usage.dailySpend}
              title="Daily Spend"
              color="var(--accent-green)"
            />
          </div>
        ) : (
          <div className="settings-card settings-card--empty">No usage data available</div>
        )}
        {usage && (
          <div className="dashboard-kpis" style={{ marginTop: 12 }}>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">${(usage.totalSpend ?? 0).toFixed(2)}</span>
              <span className="dashboard-kpi__label">Total Spend</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">{(usage.totalTokens ?? 0).toLocaleString()}</span>
              <span className="dashboard-kpi__label">Total Tokens</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">{(usage.totalRequests ?? 0).toLocaleString()}</span>
              <span className="dashboard-kpi__label">Total Requests</span>
            </div>
          </div>
        )}
      </section>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Project"
        wide
      >
        <ProjectForm
          project={project}
          isEdit
          onSubmit={handleUpdate}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>
    </div>
  );
}
